/**
 * Session mirror — the legacy cookie set, reproduced.
 *
 * WHY IT LOOKS LIKE THIS
 * The access token is held in memory (tokenStore.js), which means a page reload
 * loses it. Something has to survive F5, and this app deliberately mirrors to
 * the SAME 14 cookies the legacy Vue app writes at Login.vue:154-179 — same
 * names, same values, same `expires: 1` / `sameSite: strict` / `domain: amd.com`
 * attributes.
 *
 * That is a requirement, not an accident: the session is shared across AMD
 * properties, so legacy pages and other *.amd.com hosts must be able to read the
 * cookie this app writes, and vice versa. An earlier revision of this file used
 * a single same-origin `sessionStorage` key, which is tighter in scope but
 * invisible to every other app — it could not satisfy that requirement.
 *
 * WHAT IT COSTS, STATED PLAINLY
 *  - `domain: amd.com` means EVERY *.amd.com property can read this token, not
 *    just this app. Same-origin storage could not be read cross-host.
 *  - The cookies are JS-readable, so XSS in any of those pages reaches them.
 *  - They outlive the tab (one day), unlike a sessionStorage session.
 * These are the legacy semantics, adopted on purpose for parity. The real fix is
 * an httpOnly cookie set by the server, which the client cannot do for itself.
 *
 * Two deliberate departures from legacy, both in cookies.js and both so the
 * thing works at all off amd.com: `domain` and `secure` are resolved from the
 * current location instead of hardcoded. In production both resolve to exactly
 * legacy's values.
 */
import LZString from 'lz-string';

import { getCookie, removeCookie, setCookie } from './cookies';
import { cleanToken, readTokenClaims } from './jwt';

/**
 * Every cookie legacy's login writes (Login.vue:161-178), plus the two its
 * logout list mentions but its login never sets (`refresh_token` from the token
 * refresh at apiservice.js:151, and `org` — an older name for `domain`).
 *
 * This list exists so sign-out cannot drift from sign-in. Legacy maintains the
 * same list by hand in four places (Headerbar.vue:568, apiservice.js:176, :217,
 * :278); all four omit the `domain` cookie that login writes, so the previous
 * user's organisation survives logout for a day. One list cannot drift.
 */
const SESSION_COOKIES = Object.freeze([
  'jwt_token',
  'email',
  'access_token',
  'domain',
  'first_name',
  'last_name',
  'username',
  'CCA_role',
  'CCA_role_id',
  'EIA_role',
  'EIA_role_id',
  'application',
  'featuresData',
  'refresh_token',
  'org',
]);

/**
 * @typedef {object} MirroredSession
 * @property {string} token
 * @property {number} expiresAt      epoch ms; 0 means "no stated expiry"
 * @property {object|null} user      { email, firstName, lastName, org }
 * @property {object|null} featuresData  the FULL { CCA, EIA } object
 * @property {string} application    'CCA' | 'EIA'
 */

/**
 * Write the session as legacy writes it.
 *
 * Takes the RAW `UserData` / `FeaturesData` straight off getUserToken rather
 * than this app's narrowed `user`/`features`, because `CCA_role*`, `EIA_role*`
 * and `featuresData` need both product branches — the narrowed form has already
 * dropped the one for the other variant.
 *
 * @param {{ token: string, userData: object|null, featuresData: object|null,
 *           application: string }} session
 */
export function saveSession({ token, userData, featuresData, application }) {
  setCookie('jwt_token', token);
  setCookie('email', userData?.userEmail);
  setCookie('access_token', userData?.accessToken);
  setCookie('domain', userData?.org);
  setCookie('first_name', userData?.firstName);
  setCookie('last_name', userData?.lastName);
  // Legacy stores userEmail under both names and renders `username` in the
  // header — which is why the header shows an email address.
  setCookie('username', userData?.userEmail);
  setCookie('CCA_role', featuresData?.CCA?.role_name);
  setCookie('CCA_role_id', featuresData?.CCA?.role_id);
  setCookie('EIA_role', featuresData?.EIA?.role_name);
  setCookie('EIA_role_id', featuresData?.EIA?.role_id);
  setCookie('application', application);

  if (featuresData) {
    // LZString, not JSON: the permission matrix is far past the 4 KB per-cookie
    // limit uncompressed. The encoding has to match legacy exactly or the other
    // apps cannot decompress what we wrote.
    setCookie('featuresData', LZString.compressToEncodedURIComponent(JSON.stringify(featuresData)));
  }
}

/** @returns {MirroredSession | null} */
export function loadSession() {
  const rawToken = getCookie('jwt_token');
  if (!rawToken) return null;

  // Legacy does the same unwrap on the way out (`decodeURIComponent(...)
  // .replaceAll('"', '')`) because the value can come back JSON-quoted.
  const token = cleanToken(rawToken);
  if (!token) return null;

  // Expiry is derived from the JWT, never stored. Legacy has no expiry cookie
  // and adding one would put a 15th cookie on a wire format other apps parse.
  const { expiresInSeconds } = readTokenClaims(token) ?? { expiresInSeconds: 0 };
  const expiresAt = expiresInSeconds ? Date.now() + expiresInSeconds * 1000 : 0;

  // An EXPIRED token is still restored, as legacy does (its guard checks only
  // that the cookies exist): the first request refreshes it through Common
  // Services, or ends the session with "Session expired" if it cannot
  // (services/sessionRefresh.js). Dropping it here would make every reload
  // after the token's hour a sign-out, with no refresh tried.

  return {
    token,
    expiresAt,
    user: {
      email: getCookie('email') ?? '',
      firstName: getCookie('first_name') ?? '',
      lastName: getCookie('last_name') ?? '',
      org: getCookie('domain') ?? '',
    },
    featuresData: readFeaturesData(),
    application: getCookie('application') ?? '',
  };
}

/**
 * Decompress `featuresData`, treating any damage as "no permissions".
 *
 * A half-written or hand-edited cookie must not throw out of app boot, and must
 * not resolve to a partial permission object either — resolveRoles() turns null
 * into an empty role list, which denies rather than grants.
 */
function readFeaturesData() {
  const raw = getCookie('featuresData');
  if (!raw) return null;
  try {
    const json = LZString.decompressFromEncodedURIComponent(raw);
    return json ? JSON.parse(json) : null;
  } catch {
    return null;
  }
}

/**
 * Rewrite just the token cookies; each is written only when given.
 *
 * Called by setAccessToken (`token`) and by a token refresh, which also gets a
 * new `refreshToken` and writes the new token to `access_token` too — legacy's
 * refresh sets jwt_token, refresh_token and access_token (apiservice.js:147-158).
 * Both run with no identity in hand, so rewriting the whole set from here would
 * blank the identity cookies.
 */
export function patchSession({ token, refreshToken, accessToken }) {
  if (token) setCookie('jwt_token', token);
  if (refreshToken) setCookie('refresh_token', refreshToken);
  if (accessToken) setCookie('access_token', accessToken);
}

/** The Okta refresh token Common Services' sign-in leaves in the shared cookies. */
export function readRefreshToken() {
  return getCookie('refresh_token');
}

/**
 * The token in the shared jwt_token cookie, or null when it is gone.
 *
 * Gone: another tab or *.amd.com app signed out (legacy checks this before
 * every request, apiservice.js:173-174). Different from ours: another tab has
 * already refreshed it, and that newer token can be adopted instead of
 * refreshing again.
 */
export function readSessionToken() {
  const raw = getCookie('jwt_token');
  return raw ? cleanToken(raw) || null : null;
}

export function clearSession() {
  SESSION_COOKIES.forEach(removeCookie);
}
